import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Link } from 'react-router-dom';
import { SmartLink } from '@/components/shared/SmartLink';
import { siteRoute } from '@/lib/domains';
import '@/styles/faq.css';

const ease = [0.22, 1, 0.36, 1];

const CATEGORIES = [
  {
    id: 'before',
    label: 'I',
    title: 'Before you enter',
    items: [
      {
        q: 'Who is NYMPHÉ for?',
        a: "NYMPHÉ was built for trans women and femmes, and for the people who are genuinely drawn to them. It's a private space — not a marketplace, not a niche. A place where the dynamic is centred from the start.",
      },
      {
        q: 'How do I request access?',
        a: "Access begins on the Access page. You share a short note about yourself and what brings you here. There is no scoring and no form to game — we read what you send and invite thoughtfully.",
      },
      {
        q: 'Is access limited right now?',
        a: 'NYMPHÉ is currently opening in stages during early access. That allows us to shape the experience carefully, keep the space intentional, and make sure access expands in the right way. It isn\'t "invite-only." It\'s a gradual release.',
      },
      {
        q: 'When will I hear back?',
        a: "You'll receive an email the moment your invite is ready. Most requests are reviewed within days, though some take longer during a curated intake. You don't need to follow up — we will.",
      },
      {
        q: 'Do I need to verify my identity?',
        a: "Verification features may be introduced or expanded over time. Where available, they are designed to support trust and safety, but no verification process can guarantee a person's identity, intentions, or behaviour.",
      },
    ],
  },
  {
    id: 'inside',
    label: 'II',
    title: 'Inside NYMPHÉ',
    items: [
      {
        q: 'How is this different from other apps?',
        a: "We don't do swipes, scores, or algorithmic noise. Discovery is about presence, not a feed. Messages don't open until interest is mutual. It's slower by design — and it's meant to be.",
      },
      {
        q: 'What does "mutual" mean here?',
        a: "Conversations don't exist until both people have opted in. No cold DMs. No unwanted openers. When a thread opens, both of you have already chosen it.",
      },
      {
        q: 'Can I control who sees me?',
        a: 'Yes. Visibility, presence, and access are each decided by you. You can appear, soften, or step away without explanation — and nothing opens unless you want it to.',
      },
      {
        q: 'Can admirers and trans women both join?',
        a: "Yes. NYMPHÉ was designed for both sides of this dynamic, with clarity on both sides. Admirers enter with intention; trans women and femmes are centred, not accommodated.",
      },
      {
        q: 'Is there a mobile app?',
        a: "The web experience is where NYMPHÉ lives today. Native apps are being prepared — members will be invited to them first.",
      },
    ],
  },
  {
    id: 'safety',
    label: 'III',
    title: 'Safety & privacy',
    items: [
      {
        q: 'How do you handle reports?',
        a: "Reports are read by a real person, not a queue. We act quickly and communicate what happens. Members who breach our standards lose access — we don't negotiate on this.",
      },
      {
        q: 'Is my profile private?',
        a: "Nothing you share is indexed by search engines, and your profile is only visible inside NYMPHÉ. You choose what appears, to whom, and when.",
      },
      {
        q: 'What data do you collect?',
        a: "Only what's necessary to run the platform and protect the space — account details, what you choose to share, and essential usage signals. The full detail lives in our Privacy Policy.",
      },
      {
        q: 'Do you share information with third parties?',
        a: "We do not intend to sell your data. We work with service providers that help operate NYMPHÉ \u2014 such as hosting, email delivery, analytics, and payment services \u2014 and use contractual terms designed to protect user information where appropriate.",
      },
      {
        q: 'What happens if I leave?',
        a: "You can request account deletion at any time. We take reasonable steps to remove or de-identify account information, subject to technical, legal, safety, fraud-prevention, and record-keeping requirements.",
      },
    ],
  },
  {
    id: 'billing',
    label: 'IV',
    title: 'Billing',
    items: [
      {
        q: 'What are the membership layers?',
        a: "Three: Silk, Velvet, and Noir. Silk is the considered baseline. Velvet opens more of the experience. Noir is the most private layer, offered in limited numbers.",
      },
      {
        q: 'Monthly or annual?',
        a: "Both. Annual is priced so the commitment meets the experience, without turning it into a discount game. You can switch between the two from your account.",
      },
      {
        q: 'Will I be charged during early access?',
        a: "Early members receive a standing rate that reflects entering at this stage. What you're offered at invite is what you'll be billed — nothing changes quietly later.",
      },
      {
        q: 'Can I cancel anytime?',
        a: "Yes. Cancellation is one step from your account. Your layer stays active until the end of the period you've paid for — no clawback, no hidden friction.",
      },
      {
        q: 'Which payment methods do you accept?',
        a: "Payments are processed by third-party payment providers. Billing details and descriptors may depend on the provider, your bank, and the payment method used.",
      },
    ],
  },
];

const FAQItem = ({ item, categoryId, index, isOpen, onToggle }) => {
  const itemId = `${categoryId}-${index}`;
  return (
    <div
      className={`faq-item ${isOpen ? 'faq-item--open' : ''}`}
      data-testid={`faq-item-${itemId}`}
    >
      <button
        type="button"
        className="faq-item-trigger"
        onClick={onToggle}
        aria-expanded={isOpen}
        aria-controls={`faq-answer-${itemId}`}
        data-testid={`faq-trigger-${itemId}`}
      >
        <span className="faq-item-q">{item.q}</span>
        <span className="faq-item-icon" aria-hidden="true">
          <span className="faq-item-icon-bar" />
          <span className="faq-item-icon-bar faq-item-icon-bar--v" />
        </span>
      </button>
      <AnimatePresence initial={false}>
        {isOpen && (
          <motion.div
            key="content"
            id={`faq-answer-${itemId}`}
            className="faq-item-answer-wrap"
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.42, ease }}
          >
            <p className="faq-item-a" data-testid={`faq-answer-${itemId}`}>
              {item.a}
            </p>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

const FAQPage = () => {
  // Single-open-at-a-time accordion; "catId:index" key
  const [openKey, setOpenKey] = useState(null);

  const toggle = (key) => setOpenKey((prev) => (prev === key ? null : key));

  return (
    <div className="faq-page" data-testid="faq-page">
      {/* ── HERO ── */}
      <section className="faq-hero" data-testid="faq-hero">
        <img
          src="/images/faq-hero.jpg"
          alt=""
          className="faq-hero-img"
          data-testid="faq-hero-img"
        />
        <div className="faq-hero-overlay" aria-hidden="true" />
        <div className="faq-hero-grain" aria-hidden="true" />

        <div className="faq-hero-inner">
          <motion.p
            className="faq-hero-label"
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.15, duration: 0.7, ease }}
            data-testid="faq-hero-label"
          >
            FAQ
          </motion.p>

          <motion.h1
            className="faq-hero-headline"
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3, duration: 0.95, ease }}
            data-testid="faq-hero-headline"
          >
            Questions, answered clearly.
          </motion.h1>

          <motion.p
            className="faq-hero-sub"
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.5, duration: 0.85, ease }}
            data-testid="faq-hero-sub"
          >
            Everything you need to know before you step inside NYMPHÉ.
          </motion.p>

          <motion.p
            className="faq-hero-date"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.7, duration: 0.7, ease }}
            data-testid="faq-hero-date"
          >
            Last updated: April 2026
          </motion.p>
        </div>
      </section>

      {/* ── CONTENT ── */}
      <section className="faq-content" data-testid="faq-content">
        <div className="faq-content-inner">
          {CATEGORIES.map((cat) => (
            <div
              key={cat.id}
              className="faq-category"
              data-testid={`faq-category-${cat.id}`}
            >
              <div className="faq-category-header">
                <span className="faq-category-numeral" aria-hidden="true">
                  {cat.label}
                </span>
                <h2 className="faq-category-title">{cat.title}</h2>
              </div>

              <div className="faq-list">
                {cat.items.map((item, i) => {
                  const key = `${cat.id}:${i}`;
                  return (
                    <FAQItem
                      key={key}
                      item={item}
                      categoryId={cat.id}
                      index={i}
                      isOpen={openKey === key}
                      onToggle={() => toggle(key)}
                    />
                  );
                })}
              </div>
            </div>
          ))}

          {/* Still have questions — subtle closing */}
          <div className="faq-closing" data-testid="faq-closing">
            <p className="faq-closing-line">Still something you'd like to ask?</p>
            <Link
              to="/contact"
              className="faq-closing-link"
              data-testid="faq-closing-contact"
            >
              Write to us <span aria-hidden="true">→</span>
            </Link>
            <SmartLink
              to={siteRoute('/access')}
              className="faq-closing-link faq-closing-link--alt"
              data-testid="faq-closing-access"
            >
              Request access <span aria-hidden="true">→</span>
            </SmartLink>
          </div>
        </div>
      </section>
    </div>
  );
};

export default FAQPage;
